import { CommonModule } from '@angular/common';
import { Component, OnInit, Signal, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';

import { Store } from '@ngrx/store';
import { firstValueFrom, skipWhile, take } from 'rxjs';
import { AuthState, selectHasRoles } from '../../auth/auth.reducer';
import { LayoutService } from '../layout.service';
import { SidebarState } from './sidebar-state';

@Component({
  selector: 'carehub-sidebar',
  standalone: true,
  imports: [CommonModule, ButtonModule, RouterModule, MenuModule, DrawerModule, TooltipModule],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit {
  sidebarState: Signal<SidebarState> = signal('narrow');
  menuItems: MenuItem[] = [];

  constructor(
    private _layoutService: LayoutService,
    private _store: Store<AuthState>
  ) {}

  async checkAccess(roles: string[]): Promise<boolean> {
    const hasAccess: boolean | undefined = await firstValueFrom(
      this._store.select(selectHasRoles(roles)).pipe(
        skipWhile(hasAccess => hasAccess === undefined),
        take(1)
      )
    );

    if (hasAccess === undefined) {
      return false;
    }

    return hasAccess;
  }

  async ngOnInit(): Promise<void> {
    this.sidebarState = this._layoutService.sidebarState;

    this.menuItems = [
      {
        label: 'Utilities',
        items: [
          {
            label: $localize`:@@sidebar.utilities.service_requests:Service Requests`,
            icon: 'pi pi-ticket',
            routerLink: '/utilities/service-requests',
          },
          {
            label: $localize`:@@sidebar.utilities.andon:Andon`,
            visible: await this.checkAccess([
              'SuperAdmin',
              'Operations.Admin',
              'Operations.AndonWorkstation',
              'Operations.AndonDashboard',
            ]),
            icon: 'pi pi-chart-scatter',
            items: [
              {
                label: $localize`:@@sidebar.andon.services:Services`,
                visible: await this.checkAccess(['SuperAdmin', 'Operations.Admin']),
                routerLink: '/andon/administration/services',
              },
              {
                label: $localize`:@@sidebar.andon.workstations:Workstations`,
                visible: await this.checkAccess(['SuperAdmin', 'Operations.Admin']),
                routerLink: '/andon/administration/workstations',
              },
              {
                label: $localize`:@@sidebar.andon.dashboards:Dashboards`,
                visible: await this.checkAccess(['SuperAdmin', 'Operations.Admin']),
                routerLink: '/andon/administration/dashboards',
              },
              {
                label: $localize`:@@sidebar.andon.users:Users`,
                visible: await this.checkAccess(['SuperAdmin', 'Operations.Admin']),
                routerLink: '/andon/administration/users',
              },
              {
                label: $localize`:@@sidebar.andon.locations:Locations`,
                visible: await this.checkAccess(['SuperAdmin', 'Operations.Admin']),
                routerLink: '/andon/administration/locations',
              },
              {
                label: $localize`:@@sidebar.andon.workstation:Workstation`,
                visible: await this.checkAccess([
                  'SuperAdmin',
                  'Operations.Admin',
                  'Operations.AndonWorkstation',
                ]),
                routerLink: '/andon/workstation',
              },
              {
                label: $localize`:@@sidebar.andon.dashboard:Dashboard`,
                visible: await this.checkAccess([
                  'SuperAdmin',
                  'Operations.Admin',
                  'Operations.AndonDashboard',
                ]),
                routerLink: '/andon/dashboard',
              },
              {
                label: $localize`:@@sidebar.andon.locations:Location`,
                visible: await this.checkAccess([
                  'SuperAdmin',
                  'Operations.Admin',
                  'Operations.AndonLocations',
                ]),
                routerLink: '/andon/locations',
              },
            ].filter(entry => entry.visible !== false),
          },
        ],
      },
      {
        label: 'Markets',
        items: [
          {
            label: $localize`:@@sidebar.markets.drug_list:Drug List`,
            visible: await this.checkAccess([
              'SuperAdmin',
              'Markets.Admin',
              'Markets.Purchase',
              'Markets.Sales',
              'Markets.ProductDevelopment',
            ]),
            icon: 'pi pi-search',
            routerLink: '/markets/drugs',
          },
          {
            label: $localize`:@@sidebar.markets.linking:Product Linking`,
            visible: await this.checkAccess([
              'SuperAdmin',
              'Markets.Admin',
              'Markets.ProductDevelopment',
            ]),
            icon: 'pi pi-link',
            routerLink: '/markets/linking',
          },
          {
            label: $localize`:@@sidebar.markets.sales:Sales`,
            icon: 'pi pi-chart-line',
            routerLink: '/markets/sales',
          },
          {
            label: 'Purchase',
            visible: await this.checkAccess(['SuperAdmin', 'Markets.Admin', 'Markets.Purchase']),
            icon: 'pi pi-shopping-cart',
            items: [
              {
                label: $localize`:@@sidebar.markets.purchase_planner:Price and Availability`,
                routerLink: '/markets/price-and-availability',
              },
              {
                label: $localize`:@@sidebar.markets.purchase_planner:Purchase Planner`,
                routerLink: '/markets/purchase-planner',
              },
            ],
          },
          {
            label: 'Notes',
            visible: await this.checkAccess([
              'SuperAdmin',
              'Markets.Admin',
              'Markets.Purchase',
              'Markets.Sales',
              'Markets.ProductDevelopment',
            ]),
            icon: 'pi pi-clipboard',
            items: [
              {
                label: $localize`:@@sidebar.notes.notes:Notes`,
                visible: await this.checkAccess([
                  'SuperAdmin',
                  'Markets.Admin',
                  'Markets.Purchase',
                  'Markets.Sales',
                  'Markets.ProductDevelopment',
                ]),
                routerLink: '/markets/notes/list',
              },
              {
                label: $localize`:@@sidebar.notes.templates:Note templates`,
                visible: await this.checkAccess(['SuperAdmin', 'Markets.Admin']),
                routerLink: '/markets/notes/templates',
              },
              {
                label: $localize`:@@sidebar.notes.categories:Note categories`,
                visible: await this.checkAccess(['SuperAdmin', 'Markets.Admin']),
                routerLink: '/markets/notes/categories',
              },
            ],
          },
          {
            label: $localize`:@@sidebar.markets.product_list:Product List`,
            visible: await this.checkAccess([
              'SuperAdmin',
              'Markets.Admin',
              'Markets.Purchase',
              'Markets.Sales',
              'Markets.ProductDevelopment',
            ]),
            icon: 'pi pi-list',
            routerLink: '/markets/products',
          },
        ],
      },
      {
        label: 'Documents',
        visible: await this.checkAccess(['SuperAdmin', 'Quality.Admin']),
        items: [
          {
            label: $localize`:@@sidebar.documents.document_settings:Document Settings`,
            icon: 'pi pi-file-edit',
            routerLink: '/documents/settings',
          },
        ],
      },
      {
        label: 'Administration',
        visible: await this.checkAccess(['SuperAdmin', 'Operations.Admin', 'Quality.Admin']),
        items: [
          {
            label: $localize`:@@sidebar.administration.users:Users`,
            icon: 'pi pi-user-edit',
            routerLink: '/user-administration',
          },
        ],
      },
      {
        label: 'Profile',
        items: [
          {
            label: $localize`:@@sidebar.profile.settings:Settings`,
            icon: 'pi pi-cog',
            routerLink: '/settings',
          },
        ],
      },
    ].filter(entry => entry.visible !== false);
  }
}
